import React, { useState } from 'react';
import { 
  Typography, 
  TextField, 
  Button, 
  Box,
  Alert,
  InputAdornment,
  IconButton,
  Stack,
  Fade,
  Grow,
  Grid,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Link
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Email, 
  Lock,
  Person,
  CreditCard
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import logo from '../../assets/logo.png';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

const MotionBox = motion(Box);

function Register() {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [kvkkAccepted, setKvkkAccepted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!kvkkAccepted) {
      setError('KVKK metnini kabul etmelisiniz');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      return setError('Şifreler eşleşmiyor');
    }

    try {
      setError('');
      setLoading(true);
      
      const userCredential = await signup(
        formData.email, 
        formData.password, 
        formData.username, 
        formData.referralCode
      );

      const userRef = doc(db, 'users', userCredential.user.uid);
      await updateDoc(userRef, {
        paymentStatus: 'pending',
        subscriptionType: 'free',
        subscriptionEndDate: null,
        createdAt: new Date(),
        updatedAt: new Date(),
        kvkkAccepted: true,
        kvkkAcceptedAt: new Date()
      });

      navigate('/premium-package');
    } catch (error) {
      console.error("Signup error:", error);
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('Bu email adresi zaten kullanımda');
          break;
        case 'auth/invalid-email':
          setError('Geçersiz email adresi');
          break;
        case 'auth/operation-not-allowed':
          setError('Email/şifre girişi devre dışı bırakılmış');
          break;
        case 'auth/weak-password':
          setError('Şifre çok zayıf. En az 6 karakter kullanın');
          break;
        default:
          setError(`Hesap oluşturulamadı: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const openKvkkPdf = () => {
    window.open('/kvkk.pdf', '_blank');
  };

  return (
    <Fade in timeout={800}>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <MotionBox 
          sx={{ mb: 4, textAlign: 'center' }}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.img 
            src={logo} 
            alt="AirUpEnglish Logo" 
            style={{ 
              height: 50,
              marginBottom: 24,
              width: 'auto'
            }}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          />
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              background: 'linear-gradient(45deg, #00bf0d, #4cf13d)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Create Account
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ opacity: 0.8 }}
          >
            Start your language learning journey
          </Typography>
        </MotionBox>

        {error && (
          <Grow in>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          </Grow>
        )}

        <Stack spacing={3}>
          <MotionBox
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <TextField
              fullWidth
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
          </MotionBox>

          <MotionBox
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
          </MotionBox>

          <MotionBox
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
          </MotionBox>

          <MotionBox
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
          </MotionBox>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Referans Kodu (İsteğe Bağlı)"
              name="referralCode"
              value={formData.referralCode}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CreditCard sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <MotionBox
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.65 }}
            sx={{ mt: 2, mb: 2 }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={kvkkAccepted}
                  onChange={(e) => setKvkkAccepted(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={openKvkkPdf}
                    sx={{ 
                      textDecoration: 'none',
                      color: 'primary.main',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    KVKK Aydınlatma Metni
                  </Link>
                  'ni okudum ve kabul ediyorum
                </Typography>
              }
            />
            {!kvkkAccepted && error === 'KVKK metnini kabul etmelisiniz' && (
              <FormHelperText error>
                KVKK metnini kabul etmelisiniz
              </FormHelperText>
            )}
          </MotionBox>

          <MotionBox
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                height: 56,
                background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)',
                boxShadow: '0 3px 5px 2px rgba(0, 191, 13, .3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #00bf0d 60%, #4cf13d 90%)',
                  boxShadow: '0 4px 10px 2px rgba(0, 191, 13, .4)',
                }
              }}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </MotionBox>
        </Stack>

        <MotionBox 
          sx={{ mt: 3, textAlign: 'center' }}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Button
              variant="text"
              onClick={() => navigate('/login')}
              sx={{ 
                fontWeight: 600,
                '&:hover': {
                  background: 'rgba(0, 191, 13, 0.1)',
                }
              }}
            >
              Sign In
            </Button>
          </Typography>
        </MotionBox>
      </Box>
    </Fade>
  );
}

export default Register;
